import { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import get from 'transmute/get';
// @ts-expect-error module is not typed
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import styled from 'styled-components';
import { DISTANCE_MEASUREMENT_SMALL, DROPDOWN_PADDING_Y, NAV_ITEM_PADDING_RIGHT, NAV_ITEM_PADDING_LEFT, SELECTABLE_BUTTON_MAX_WIDTH, DROPDOWN_ITEM_DEFAULT_HEIGHT } from 'HubStyleTokens/sizes';
import { WEB_FONT_MEDIUM_WEIGHT } from 'HubStyleTokens/misc';
import { CALYPSO_LIGHT, BATTLESHIP } from 'HubStyleTokens/colors';
import { getFromNumberDescription } from 'calling-ui-library/utils/phoneNumberSourceUtils';
import PortalIdParser from 'PortalIdParser';
import { getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getCallProvidersFromOmnibusResult, getConnectAccountNumbers, getHubSpotCallingEnabled, getHubspotProvidedNumbers, getHubspotSubAccountNumbers, getInstalledCallingSettings, getPhoneNumbers } from '../../callee-omnibus/operators/settingsOmnibusResultGetters';
import { HUBSPOT, TWILIO } from 'calling-internal-common/call-provider/constants/ProviderNames';
import { getIsProviderHubSpot, getIsTwilioBasedCallProvider } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import { isCallingFree, isStarterOrPro } from 'calling-internal-common/utils/CallingSalesPro';
import { getCallingMinutesPerPortal } from 'calling-internal-common/utils/getCallingLimits';
import { upgradeSalesStarterUrl } from 'calling-lifecycle-internal/utils/urlUtils';
import getAppUrls from 'calling-orchestration-shared-library/constants/appUrls';
import { PhoneNumberSources } from 'calling-internal-common/constants/PhoneNumberSources';
import { WEB_FONT_REGULAR_WEIGHT } from 'HubStyleTokens/misc';
import { BASE_FONT_COLOR } from 'HubStyleTokens/theme';
import FormattedMessage from 'I18n/components/FormattedMessage';
// @ts-expect-error module is not typed
import DropdownMenuOption from 'UIComponents/dropdown/internal/DropdownMenuOption';
import UIList from 'UIComponents/list/UIList';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import Small from 'UIComponents/elements/Small';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_OPTIONS = 4;
const SectionTitle = styled.div.withConfig({
  displayName: "PhoneFromNumberOptions__SectionTitle",
  componentId: "sc-1sq5l0y-0"
})(["cursor:pointer;padding:", " 0 ", " 0;min-width:", ";background-color:transparent;&.is--disabled{opacity:0.5;}&:hover{background-color:", ";}"], DROPDOWN_PADDING_Y, DROPDOWN_PADDING_Y, SELECTABLE_BUTTON_MAX_WIDTH, CALYPSO_LIGHT);
const StyledFormattedMessage = styled(FormattedMessage).withConfig({
  displayName: "PhoneFromNumberOptions__StyledFormattedMessage",
  componentId: "sc-1sq5l0y-1"
})(["padding-left:", ";font-weight:", ";"], DISTANCE_MEASUREMENT_SMALL, WEB_FONT_MEDIUM_WEIGHT);
const StyledUIIcon = styled(UIIcon).withConfig({
  displayName: "PhoneFromNumberOptions__StyledUIIcon",
  componentId: "sc-1sq5l0y-2"
})(["float:right;padding-right:", ";"], NAV_ITEM_PADDING_RIGHT);
const ErrorWrapper = styled.div.withConfig({
  displayName: "PhoneFromNumberOptions__ErrorWrapper",
  componentId: "sc-1sq5l0y-3"
})(["max-width:", ";padding-right:", ";padding-left:", ";"], SELECTABLE_BUTTON_MAX_WIDTH, NAV_ITEM_PADDING_RIGHT, NAV_ITEM_PADDING_LEFT);
const StyledUIList = styled(UIList).withConfig({
  displayName: "PhoneFromNumberOptions__StyledUIList",
  componentId: "sc-1sq5l0y-4"
})(["overflow-y:auto;max-height:calc((", " + 0.5) * ", ");border-bottom:", ";"], MAX_OPTIONS, DROPDOWN_ITEM_DEFAULT_HEIGHT, ({
  useborder
}) => useborder && `1px solid ${BATTLESHIP}`);
const StyledDropdownMenuOption = styled(DropdownMenuOption).withConfig({
  displayName: "PhoneFromNumberOptions__StyledDropdownMenuOption",
  componentId: "sc-1sq5l0y-5"
})(["width:100%;cursor:pointer;&&{min-width:", ";color:", ";font-weight:", ";}&&:hover{color:", ";text-decoration:none;}&:hover{background-color:", ";}"], SELECTABLE_BUTTON_MAX_WIDTH, BASE_FONT_COLOR, WEB_FONT_REGULAR_WEIGHT, BASE_FONT_COLOR, CALYPSO_LIGHT);
function getPhoneFromNumberOptions(providerName, accountNumbers, scopes) {
  return accountNumbers.reduce((acc, fromNumber) => {
    const isFreeTier = isCallingFree(scopes);
    const isFreePortal = !isStarterOrPro(scopes);
    const isHubSpotNumber = fromNumber.source === PhoneNumberSources.HUBSPOT_NUMBER;
    const key = {
      providerName,
      fromNumber
    };
    const disabled = isHubSpotNumber && (isFreeTier || isFreePortal);
    acc.push({
      key,
      disabled
    });
    return acc;
  }, []);
}
function getOtherCallingProvidersOptions(callingProviders) {
  return callingProviders.reduce((acc, callingProvider) => {
    const providerName = callingProvider.get('name');
    if (getIsTwilioBasedCallProvider(callingProvider)) {
      return acc;
    }
    const key = {
      providerName
    };
    acc.push({
      key
    });
    return acc;
  }, []);
}
const OTHER_PROVIDERS = 'OtherProviders';
function PhoneFromNumberOptions({
  asyncSettingsOmnibus,
  selectedProviderName,
  selectedFromNumber,
  hasCallingProvidersCapability,
  onSelect,
  disabled,
  scopes,
  limits,
  enableBiDirectionalProviderSwitching
}) {
  const selectedOption = {
    providerName: selectedProviderName,
    fromNumber: selectedFromNumber
  };
  const selectedProvider = {
    name: selectedProviderName
  };
  const [showHubSpotSection, setShowHubSpotSection] = useState(getIsProviderHubSpot(selectedProvider));
  const [showTwilioSection, setShowTwilioSection] = useState(getIsTwilioBasedCallProvider(selectedProvider) && !getIsProviderHubSpot(selectedProvider));
  const [showOtherProvidersSection, setShowOtherProvidersSection] = useState(!enableBiDirectionalProviderSwitching && !getIsTwilioBasedCallProvider(selectedProvider));
  const data = getData(asyncSettingsOmnibus);
  const fromNumbers = getPhoneNumbers(data);
  const hubspotSubAccountNumbers = getHubspotSubAccountNumbers(fromNumbers);
  const hubspotProvidedNumbers = getHubspotProvidedNumbers(fromNumbers);
  const hubspotNumbers = hubspotSubAccountNumbers.concat(hubspotProvidedNumbers);
  const hubspotOptions = useMemo(() => getPhoneFromNumberOptions(HUBSPOT, hubspotNumbers, scopes), [hubspotNumbers, scopes]);
  const connectAccountNumbers = getConnectAccountNumbers(fromNumbers);
  const twilioOptions = useMemo(() => getPhoneFromNumberOptions(TWILIO, connectAccountNumbers, scopes), [connectAccountNumbers, scopes]);
  const callingProviders = getCallProvidersFromOmnibusResult(data);
  const otherCallingProvidersOptions = useMemo(() => {
    return getOtherCallingProvidersOptions(callingProviders);
  }, [callingProviders]);
  const otherCallingProvidersError = useMemo(() => get('error', getInstalledCallingSettings(data)), [data]);
  const isFreeTier = useMemo(() => isCallingFree(scopes), [scopes]);
  const isPaidPortal = useMemo(() => getCallingMinutesPerPortal(limits), [limits]);
  const phoneFromNumberSections = useMemo(() => [...(!getHubSpotCallingEnabled(data) ? [] : [{
    key: HUBSPOT,
    title: 'callWidgetProviders.HubSpot.widgetTitle',
    options: hubspotOptions,
    providerName: HUBSPOT,
    show: showHubSpotSection,
    toggleShowSection: override => setShowHubSpotSection(override && !showHubSpotSection),
    disabled: !isPaidPortal || isFreeTier
  }]), {
    key: TWILIO,
    title: 'callWidgetProviders.TwilioConnect.widgetTitle',
    options: twilioOptions,
    providerName: TWILIO,
    show: showTwilioSection,
    toggleShowSection: override => setShowTwilioSection(override && !showTwilioSection)
  }, ...(enableBiDirectionalProviderSwitching ? [] : [{
    key: OTHER_PROVIDERS,
    title: 'callWidgetProviders.OtherProviders.widgetTitle',
    options: otherCallingProvidersOptions,
    show: showOtherProvidersSection,
    toggleShowSection: override => setShowOtherProvidersSection(override && !showOtherProvidersSection),
    error: otherCallingProvidersError && 'callWidgetProviders.OtherProviders.error'
  }])], [data, hubspotOptions, twilioOptions, otherCallingProvidersOptions, otherCallingProvidersError, showHubSpotSection, showTwilioSection, showOtherProvidersSection, setShowHubSpotSection, setShowTwilioSection, setShowOtherProvidersSection, isPaidPortal, isFreeTier, enableBiDirectionalProviderSwitching]);
  const handleToggleSection = useCallback(key => {
    phoneFromNumberSections.map(section => {
      const toggle = section.key === key;
      return section.toggleShowSection(toggle);
    });
  }, [phoneFromNumberSections]);
  const portalId = useMemo(() => PortalIdParser.get(), []);
  const title = useMemo(() => {
    const {
      paidUsersManagementUrl
    } = getAppUrls();
    return isPaidPortal ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: 'phoneFromNumberOptions.seatRequiredTooltip_jsx',
      elements: {
        UILink
      },
      options: {
        href: paidUsersManagementUrl,
        target: '_blank'
      }
    }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: 'phoneFromNumberOptions.upgradeTooltip_jsx',
      elements: {
        UILink
      },
      options: {
        href: upgradeSalesStarterUrl(portalId),
        target: '_blank'
      }
    });
  }, [isPaidPortal, portalId]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: phoneFromNumberSections.map((section, index) => {
      const shouldShowWhenDisabled = section.key === HUBSPOT && isFreeTier;
      if (!shouldShowWhenDisabled && !section.options.length && !section.error || !hasCallingProvidersCapability && section.key === OTHER_PROVIDERS) {
        return null;
      }
      const overflowAndNotLast = section.options.length > MAX_OPTIONS && index !== phoneFromNumberSections.length - 1;
      return /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(UITooltip, {
          title: title,
          disabled: !section.disabled,
          "data-test-id": `pre-call-section-${section.key}-tooltip`,
          children: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsxs(SectionTitle, {
              onClick: () => !section.disabled && handleToggleSection(section.key),
              className: section.disabled ? 'is--disabled' : '',
              "data-selenium-test": `pre-call-section-${section.key}`,
              "data-unit-test": `pre-call-section-${section.key}`,
              children: [/*#__PURE__*/_jsx(StyledFormattedMessage, {
                message: section.title
              }), /*#__PURE__*/_jsx(StyledUIIcon, {
                name: section.show ? 'up' : 'down',
                size: "xxs"
              })]
            }), section.show && (section.error ? /*#__PURE__*/_jsx(ErrorWrapper, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: section.error
              })
            }) : /*#__PURE__*/_jsx(StyledUIList, {
              useborder: overflowAndNotLast ? 1 : 0,
              children: section.options.map(option => {
                const key = option.key;
                const keyOption = key;
                const selectedKeyOption = selectedOption;
                const shouldDisableTooltip = !option.disabled;
                let selected = key === selectedOption;
                let keyString = key;
                let Message = {};
                const isUsingTwilioConnect = keyOption.providerName === TWILIO;
                if (keyOption.fromNumber) {
                  const fromNumber = keyOption.fromNumber;
                  const fromNumberString = fromNumber.get('friendlyName') || '';
                  const fromNumberValue = fromNumber.phoneNumber || '';
                  const fromNumberExtension = fromNumber.extension || '';
                  const selectedFromNumberString = selectedKeyOption.fromNumber && selectedKeyOption.fromNumber.get('friendlyName');
                  selected = keyOption.providerName === selectedKeyOption.providerName && fromNumberString === selectedFromNumberString;
                  keyString = `${keyOption.providerName}_${fromNumberString}`;
                  Message = fromNumber.nickname ? /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx("span", {
                      children: fromNumber.nickname
                    }), /*#__PURE__*/_jsxs(Small, {
                      className: "display-block",
                      children: [/*#__PURE__*/_jsx(FormattedPhoneNumber, {
                        value: isUsingTwilioConnect ? fromNumberString : fromNumberValue,
                        extension: isUsingTwilioConnect ? undefined : fromNumberExtension
                      }), ' ', "(", getFromNumberDescription(fromNumber.source), ")"]
                    })]
                  }) : /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx(FormattedPhoneNumber, {
                      value: isUsingTwilioConnect ? fromNumberString : fromNumberValue,
                      extension: isUsingTwilioConnect ? undefined : fromNumberExtension
                    }), /*#__PURE__*/_jsx("div", {
                      children: option.text
                    }), /*#__PURE__*/_jsx(Small, {
                      children: getFromNumberDescription(fromNumber.source)
                    })]
                  });
                } else {
                  Message = keyOption.providerName;
                  selected = keyOption.providerName === selectedOption.providerName;
                  keyString = `${keyOption.providerName}`;
                }
                return /*#__PURE__*/_jsx(UITooltip, {
                  title: title,
                  disabled: shouldDisableTooltip,
                  placement: "bottom",
                  children: /*#__PURE__*/_jsx(StyledDropdownMenuOption, {
                    selected: selected,
                    onClick: () => onSelect(key),
                    disabled: disabled || option.disabled,
                    "data-selenium-test": `pre-call-option-${keyString}`,
                    children: Message
                  }, keyString)
                }, keyString);
              })
            }))]
          })
        })
      }, section.key);
    })
  });
}
PhoneFromNumberOptions.propTypes = {
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  selectedProviderName: PropTypes.string.isRequired,
  selectedFromNumber: RecordPropType('FromNumber'),
  hasCallingProvidersCapability: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  scopes: ImmutablePropTypes.map.isRequired,
  limits: ImmutablePropTypes.map.isRequired
};
export default /*#__PURE__*/memo(PhoneFromNumberOptions);